import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useAppUser } from '@/contexts/AppUserProvider';
import { DistributionGroup } from '@/types/distributionGroupTypes';
import {
  getDistributionGroups,
  deleteDistributionGroup,
  updateDistributionGroup
} from '@/services/distributionGroupService';
import { DistributionGroupDialog } from '@/components/DistributionGroups/DistributionGroupDialog';
import { DeleteConfirmDialog } from '@/components/DistributionGroups/DeleteConfirmDialog';
import { UnifiedTabs, UnifiedTabsContainer } from '@/components/UnifiedTabs';
import { UsersThree } from '@phosphor-icons/react';

const DistributionGroupDetailPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { selectedUserRole } = useAppUser();
  const { setHeaderText, setMenuActions } = useHeaderContext();

  const [group, setGroup] = useState<DistributionGroup | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  // const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'recipients' | 'notes'
  >('overview');

  useEffect(() => {
    const fetchGroup = async () => {
      if (!groupId) return;
      const companyId = selectedUserRole?.companyId;
      if (!companyId) return;

      setIsLoading(true);
      try {
        const groups = await getDistributionGroups(
          companyId,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const foundGroup = groups.find((g) => g.id === parseInt(groupId));
        if (foundGroup) {
          setGroup(foundGroup);
        } else {
          setError('Distribution group not found');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load distribution group');
        console.error('Error fetching distribution group:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroup();
  }, [
    groupId,
    selectedUserRole,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Set header content (mobile-friendly)
  useEffect(() => {
    if (group) {
      // Mobile: Just show group name (back button will be positioned separately), desktop: show breadcrumb (Attio style)
      const headerContent = isMobile ? group.name : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            onClick={() => navigate('/organization?tab=distribution-groups')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'pointer',
              transition: 'color 0.15s ease',
              '&:hover': { 
                color: theme.palette.text.primary 
              }
            }}
          >
            <UsersThree size={16} weight="regular" />
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'inherit'
              }}
            >
              Distribution Groups
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '14px',
              color: theme.palette.text.disabled,
              userSelect: 'none',
              mx: -0.25
            }}
          >
            /
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: theme.palette.text.primary,
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {group.name}
          </Typography>
        </Box>
      );
      setHeaderText(headerContent);
      setMenuActions(
        isMobile ? (
          // Mobile: Three dots menu in header
          <IconButton
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : '#f3f4f6'
              }
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ) : (
          // Desktop: Show Edit Group button
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 500,
              px: 3
            }}
          >
            Edit Group
          </Button>
        )
      );
    } else {
      setHeaderText('Distribution Group');
      setMenuActions(null);
    }

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [group, navigate, setHeaderText, setMenuActions, isMobile]);

  // const handleTestEmail = async () => { /* removed with Actions */ };

  const handleDelete = async () => {
    if (!group) return;

    try {
      await deleteDistributionGroup(
        { id: group.id },
        selectedUserRole?.companyId || '',
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Navigate back to organization page (preserve Distribution Groups tab)
      navigate('/organization?tab=distribution-groups');
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Distribution group not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/organization?tab=distribution-groups')}
        >
          Back to Organization
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white !important'
      }}
    >
      {/* Mobile Back Button */}
      {isMobile && (
        <IconButton
          onClick={() => navigate('/organization?tab=distribution-groups')}
          sx={{
            position: 'fixed',
            top: 6,
            left: 76, // Position after hamburger menu + spacing
            zIndex: 1301,
            color: '#6b7280',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      {/* Header */}
      <Box
        sx={{
          background: 'white',
          px: 4,
          pt: 3,
          pb: 0,
          borderBottom: `1px solid #E5E5E5`
        }}
      >
        <Box sx={{ mb: 2 }}>
          {/* Group Name and quick info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#111827',
                textAlign: 'left'
              }}
            >
              {group.name}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={group.isActive}
                  disabled
                  size="small"
                  color="default"
                />
              }
              label={group.isActive ? 'Active' : 'Inactive'}
              sx={{
                m: 0,
                alignItems: 'center',
                '& .MuiFormControlLabel-label': {
                  fontSize: '12px',
                  color: '#6b7280'
                }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ fontSize: '16px', color: '#9ca3af' }} />
              <Typography
                sx={{ fontSize: '14px', color: '#6b7280', textAlign: 'left' }}
              >
                {group.emailAddresses.length} recipient
                {group.emailAddresses.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ fontSize: '16px', color: '#9ca3af' }} />
              <Typography
                sx={{ fontSize: '14px', color: '#6b7280', textAlign: 'left' }}
              >
                Created {formatDate(group.createdAtUtc)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <UnifiedTabsContainer>
          <UnifiedTabs
            tabs={[
              { key: 'overview', label: 'Overview' },
              { key: 'notes', label: 'Notes' }
            ]}
            activeTab={activeTab}
            onTabChange={(key) => setActiveTab(key as 'overview' | 'notes')}
          />
        </UnifiedTabsContainer>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          px: 0,
          pt: 0,
          pb: 0,
          flex: 1,
          overflow: 'auto',
          background: theme.palette.background.paper
        }}
      >
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2
            }}>
              {/* Left Column - Details */}
              <Card sx={{ flex: 1, mb: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: '24px' }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      textAlign: 'left'
                    }}
                  >
                    Details
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                      <Box sx={{ mb: 2.5 }}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'left'
                          }}
                        >
                          PRIMARY EMAIL
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#1A1A1A',
                            textAlign: 'left'
                          }}
                        >
                          {group.emailAddresses[0] || '—'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'left'
                          }}
                        >
                          CREATED
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#1A1A1A',
                            textAlign: 'left'
                          }}
                        >
                          {formatDate(group.createdAtUtc)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                      <Box
                        sx={{
                          mb: 2.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={group.isActive}
                              onChange={async (e) => {
                                const next = e.target.checked;
                                const updated = await updateDistributionGroup(
                                  { id: group.id, isActive: next },
                                  selectedUserRole?.companyId || '',
                                  getAccessTokenSilently,
                                  getAccessTokenWithPopup
                                );
                                setGroup(updated);
                              }}
                              color="default"
                              size="small"
                            />
                          }
                          label={group.isActive ? 'Active' : 'Inactive'}
                          sx={{
                            m: 0,
                            alignItems: 'center',
                            '& .MuiFormControlLabel-label': {
                              fontSize: '12px',
                              color: '#6b7280'
                            }
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'left'
                          }}
                        >
                          LAST UPDATED
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#1A1A1A',
                            textAlign: 'left'
                          }}
                        >
                          {formatDate(group.updatedAtUtc)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Right Column - Recipients Preview */}
              <Card sx={{ flex: 1, mb: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: '24px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 3
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#1A1A1A',
                        textAlign: 'left'
                      }}
                    >
                      Email Recipients
                    </Typography>
                    <Typography
                      onClick={() => setActiveTab('recipients')}
                      sx={{
                        fontSize: '14px',
                        color: '#4B5EDB',
                        cursor: 'pointer',
                        textAlign: 'right',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      View all
                    </Typography>
                  </Box>

                  {group.emailAddresses.length === 0 ? (
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#6b7280',
                        textAlign: 'center',
                        py: 4
                      }}
                    >
                      No email addresses yet
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {group.emailAddresses.slice(0, 5).map((email, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.05)' 
                                : '#f9fafb'
                            }
                          }}
                        >
                          <EmailIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                          <Typography
                            sx={{ fontSize: '14px', color: theme.palette.text.primary }}
                          >
                            {email}
                          </Typography>
                        </Box>
                      ))}
                      {group.emailAddresses.length > 5 && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                            textAlign: 'center',
                            mt: 1
                          }}
                        >
                          +{group.emailAddresses.length - 5} more recipients
                        </Typography>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Actions removed per request */}
          </Box>
        )}

        {/* Recipients tab removed per request (overview shows recipients) */}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 0,
                py: 2,
                borderBottom: '1px solid #e5e7eb',
                mb: 3,
                mx: 0
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'text.primary',
                  pl: 3
                }}
              >
                Notes
              </Typography>
            </Box>
            <Card sx={{ mx: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
              <CardContent sx={{ p: '24px' }}>
                {group.description ? (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      p: 2,
                      backgroundColor: '#f9fafb',
                      borderRadius: 2,
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {group.description}
                  </Typography>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, color: '#6b7280' }}>
                    <Typography sx={{ fontSize: '14px', mb: 2 }}>
                      No notes yet
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                      Add a description to this group to help others understand
                      its purpose.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Edit Dialog */}
      <DistributionGroupDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={async (input) => {
          if (!group) return;
          const updated = await updateDistributionGroup(
            { ...(input as any), id: group.id },
            selectedUserRole?.companyId || '',
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
          setGroup(updated);
          setEditDialogOpen(false);
        }}
        editingGroup={group}
      />

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        groupName={group.name}
      />

      {/* Mobile Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setEditDialogOpen(true);
            setMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Edit Group
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setMenuAnchorEl(null);
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText 
            primaryTypographyProps={{ fontSize: '14px', color: 'error.main' }}
          >
            Delete Group
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DistributionGroupDetailPage;
